import { Navigate, RouterProvider, createBrowserRouter } from "react-router-dom";

import RootLayout from "@/layouts/root-layout";
import { RequireAdmin, RequireAdmin1, RequireAuth, RequireVendor } from "@/components/route-guards";
import { ErrorBoundaryPage, NotFoundPage } from "@/pages/error-pages";
import { InfoPage } from "@/pages/info-page";
import { HomePage } from "@/pages/home-page";
import { CartPage } from "@/pages/cart-page";
import { CheckoutPage } from "@/pages/checkout-page";
import { CheckoutSuccessPage } from "@/pages/checkout-success-page";
import ComparePage from "@/pages/compare-page";
import ForgotPasswordPage from "@/pages/forgot-password-page";
import { LoginPage } from "@/pages/login-page";
import { SignupPage } from "@/pages/signup-page";
import { PrimePage } from "@/pages/prime-page";
import { ProductsPage } from "@/pages/products-page";
import { ProductDetailPage } from "@/pages/product-detail-page";
import { SearchPage } from "@/pages/search-page";
import { DashboardLayout } from "@/pages/dashboard/dashboard-layout";
import { DashboardHomePage } from "@/pages/dashboard/dashboard-home-page";
import { AddressesPage } from "@/pages/dashboard/addresses-page";
import NotificationsPage from "@/pages/dashboard/notifications-page";
import { OrdersPage } from "@/pages/dashboard/orders-page";
import { PaymentsPage } from "@/pages/dashboard/payments-page";
import { PrimeDashboardPage } from "@/pages/dashboard/prime-page";
import ProfilePage from "@/pages/dashboard/profile-page";
import SecurityPage from "@/pages/dashboard/security-page";
import WishlistPage from "@/pages/dashboard/wishlist-page";
import { AdminLayout } from "@/pages/admin/admin-layout";
import { AdminOverviewPage } from "@/pages/admin/admin-overview-page";
import { AdminProductsPage } from "@/pages/admin/admin-products-page";
import { AdminCategoriesPage } from "@/pages/admin/admin-categories-page";
import { AdminOrdersPage } from "@/pages/admin/admin-orders-page";
import { AdminUsersPage } from "@/pages/admin/admin-users-page";
import { AdminPromotionsPage } from "@/pages/admin/admin-promotions-page";
import { AdminNavigationPage } from "@/pages/admin/admin-navigation-page";
import { AdminCouponsPage } from "@/pages/admin/admin-coupons-page";
import { AdminVendorsPage } from "@/pages/admin/admin-vendors-page";
import { AdminVendorDetailPage } from "@/pages/admin/admin-vendor-detail-page";
import { AdminVendorTypesPage } from "@/pages/admin/admin-vendor-types-page";
import { AdminVendorWithdrawalsPage } from "@/pages/admin/admin-vendor-withdrawals-page";
import { VendorLayout } from "@/pages/vendor/vendor-layout";
import { VendorHomePage } from "@/pages/vendor/vendor-home-page";
import { VendorProductsPage } from "@/pages/vendor/vendor-products-page";
import { VendorProductFormPage } from "@/pages/vendor/vendor-product-form-page";
import { VendorOrdersPage } from "@/pages/vendor/vendor-orders-page";
import { VendorEarningsPage } from "@/pages/vendor/vendor-earnings-page";
import { VendorWithdrawalsPage } from "@/pages/vendor/vendor-withdrawals-page";
import { VendorReviewsPage } from "@/pages/vendor/vendor-reviews-page";
import { VendorSettingsPage } from "@/pages/vendor/vendor-settings-page";
import { SellPage } from "@/pages/sell-page";
import { StorePage } from "@/pages/store-page";
import { StoresPage } from "@/pages/stores-page";

/**
 * The route table, replacing the App Router's file-based routing.
 *
 * Paths, nesting and the order of the checks match the original:
 *   - `/checkout`, `/dashboard/*` and `/admin/*` were protected by
 *     `middleware.ts` (which required a session, and an ADMIN role for `/admin`).
 *   - The admin segment uses its own portal layout, exactly like `app/admin/layout.tsx`.
 */
export const routes = [
  {
    path: "/",
    element: <RootLayout />,
    errorElement: <ErrorBoundaryPage />,
    children: [
      { index: true, element: <HomePage /> },
      { path: "products", element: <ProductsPage /> },
      { path: "products/:slug", element: <ProductDetailPage /> },
      { path: "search", element: <SearchPage /> },
      { path: "prime", element: <PrimePage /> },
      { path: "cart", element: <CartPage /> },
      { path: "sell", element: <SellPage /> },
      { path: "stores", element: <StoresPage /> },
      { path: "store/:slug", element: <StorePage /> },
      { path: "compare", element: <ComparePage /> },
      { path: "info/:slug", element: <InfoPage /> },
      { path: "checkout/success", element: <CheckoutSuccessPage /> },
      { path: "login", element: <LoginPage /> },
      { path: "signup", element: <SignupPage /> },
      { path: "forgot-password", element: <ForgotPasswordPage /> },

      // `middleware.ts` redirected anonymous visitors to /login.
      {
        element: <RequireAuth />,
        children: [
          { path: "checkout", element: <CheckoutPage /> },
          {
            path: "dashboard",
            element: <DashboardLayout />,
            children: [
              { index: true, element: <DashboardHomePage /> },
              { path: "orders", element: <OrdersPage /> },
              { path: "addresses", element: <AddressesPage /> },
              { path: "notifications", element: <NotificationsPage /> },
              { path: "payments", element: <PaymentsPage /> },
              { path: "prime", element: <PrimeDashboardPage /> },
              { path: "profile", element: <ProfilePage /> },
              { path: "security", element: <SecurityPage /> },
              { path: "wishlist", element: <WishlistPage /> },
            ],
          },
        ],
      },

      // Admin: a session was required *and* the role had to be ADMIN, otherwise
      // `app/admin/layout.tsx` redirected to "/".
      {
        path: "admin",
        element: <RequireAdmin />,
        children: [
          {
            element: <AdminLayout />,
            children: [
              { index: true, element: <AdminOverviewPage /> },
              { path: "products", element: <AdminProductsPage /> },
              { path: "categories", element: <AdminCategoriesPage /> },
              { path: "orders", element: <AdminOrdersPage /> },
              { path: "users", element: <AdminUsersPage /> },
              { path: "promotions", element: <AdminPromotionsPage /> },
              { path: "navigation", element: <AdminNavigationPage /> },
              { path: "coupons", element: <AdminCouponsPage /> },
              { path: "vendors", element: <AdminVendorsPage /> },
              { path: "vendors/:id", element: <AdminVendorDetailPage /> },
              { path: "vendor-types", element: <AdminVendorTypesPage /> },
              { path: "vendor-withdrawals", element: <AdminVendorWithdrawalsPage /> },
            ],
          },
        ],
      },

      // Second admin dashboard — identical pages/UI, scoped order rotation.
      {
        path: "admin1",
        element: <RequireAdmin1 />,
        children: [
          {
            element: <AdminLayout />,
            children: [
              { index: true, element: <AdminOverviewPage /> },
              { path: "products", element: <AdminProductsPage /> },
              { path: "categories", element: <AdminCategoriesPage /> },
              { path: "orders", element: <AdminOrdersPage /> },
              { path: "users", element: <AdminUsersPage /> },
              { path: "promotions", element: <AdminPromotionsPage /> },
              { path: "navigation", element: <AdminNavigationPage /> },
              { path: "coupons", element: <AdminCouponsPage /> },
              { path: "vendors", element: <AdminVendorsPage /> },
              { path: "vendors/:id", element: <AdminVendorDetailPage /> },
              { path: "vendor-types", element: <AdminVendorTypesPage /> },
              { path: "vendor-withdrawals", element: <AdminVendorWithdrawalsPage /> },
            ],
          },
        ],
      },

      // Vendor portal: role VENDOR (or ADMIN) required.
      {
        path: "vendor",
        element: <RequireVendor />,
        children: [
          {
            element: <VendorLayout />,
            children: [
              { index: true, element: <VendorHomePage /> },
              { path: "products", element: <VendorProductsPage /> },
              { path: "products/new", element: <VendorProductFormPage /> },
              { path: "products/:id", element: <VendorProductFormPage /> },
              { path: "orders", element: <VendorOrdersPage /> },
              { path: "earnings", element: <VendorEarningsPage /> },
              { path: "withdrawals", element: <VendorWithdrawalsPage /> },
              { path: "reviews", element: <VendorReviewsPage /> },
              { path: "settings", element: <VendorSettingsPage /> },
            ],
          },
        ],
      },

      { path: "*", element: <NotFoundPage /> },
    ],
  },
];

const router = createBrowserRouter(routes);

export function App() {
  return <RouterProvider router={router} />;
}

export { Navigate };